import { DataCloudEventData } from '~/modules/services/analyticsService';
import { AnalyticsEventTopic } from '../analyticsHelper';
import { AI_OVERVIEW_SEARCH_ELEMENT_TYPE } from '../gtmEventTypes/aiSearchConstants';
import { AI_OVERVIEW_ERROR_TOKEN_FALLBACK, AI_OVERVIEW_ERROR_TOKENS, DataCloudEventTypeValues } from './constants';
import {
    AiOverviewDataCloudEventType,
    AiOverviewEventDataCloudAnalyticsEvent,
    AiOverviewEventInteraction,
    clamp,
    isFiniteNumber,
} from './aiOverviewImpressionDataCloudEventType';

/**
 * AI Overview *interaction* events share the same
 * (topic, elementType) pair — `ANALYTICS_CLICK` + `aiOverviewSearch` — which
 * matches no GTM handler (so nothing leaks to GA4) and no other Data Cloud
 * handler. They are sub-discriminated by `event.aiOverviewAction`.
 */
const matchesAction = (event: DataCloudEventData, action: DataCloudEventTypeValues): boolean =>
    event.topic === AnalyticsEventTopic.ANALYTICS_CLICK &&
    event.elementType === AI_OVERVIEW_SEARCH_ELEMENT_TYPE &&
    event.aiOverviewAction === action;

const requiredListingCount = (event: DataCloudEventData): number => {
    return isFiniteNumber(event.aiOverviewListingCount) ? Math.max(0, event.aiOverviewListingCount) : 0;
};

/** Optional TokenCount/TTFT — set only when finite, per spec (non-finite → deleted). */
const applyOptionalTokenAndTtft = (interaction: AiOverviewEventInteraction, event: DataCloudEventData): void => {
    if (isFiniteNumber(event.aiOverviewTokenCount)) {
        interaction.TokenCount = clamp(event.aiOverviewTokenCount, 0, 100000);
    }
    if (isFiniteNumber(event.aiOverviewTtft)) {
        interaction.TTFT = clamp(event.aiOverviewTtft, 0, 60000);
    }
};

export class AiOverviewStreamCompleteDataCloudEventType extends AiOverviewDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return matchesAction(event, DataCloudEventTypeValues.streamComplete);
    }

    getBody(event: DataCloudEventData): AiOverviewEventDataCloudAnalyticsEvent {
        const baseEvent = this.baseBody(event);
        const interaction = this.buildEnvelope(event, baseEvent, DataCloudEventTypeValues.streamComplete);
        interaction.aiOverviewListingCount = requiredListingCount(event);
        interaction.aiOverviewLatencyMs = isFiniteNumber(event.aiOverviewLatencyMs)
            ? clamp(event.aiOverviewLatencyMs, 0, 120000)
            : 0;
        interaction.TokenCount = isFiniteNumber(event.aiOverviewTokenCount)
            ? clamp(event.aiOverviewTokenCount, 0, 100000)
            : 0;
        if (isFiniteNumber(event.aiOverviewTtft)) {
            interaction.TTFT = clamp(event.aiOverviewTtft, 0, 60000);
        }
        return { interaction, user: this.buildUser(baseEvent) };
    }
}

export class AiOverviewExpandDataCloudEventType extends AiOverviewDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return matchesAction(event, DataCloudEventTypeValues.expand);
    }

    getBody(event: DataCloudEventData): AiOverviewEventDataCloudAnalyticsEvent {
        const baseEvent = this.baseBody(event);
        const interaction = this.buildEnvelope(event, baseEvent, DataCloudEventTypeValues.expand);
        interaction.aiOverviewListingCount = requiredListingCount(event);
        interaction.aiOverviewExpandSource = event.aiOverviewExpandSource;
        applyOptionalTokenAndTtft(interaction, event);
        return { interaction, user: this.buildUser(baseEvent) };
    }
}

export class AiOverviewCollapseDataCloudEventType extends AiOverviewDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return matchesAction(event, DataCloudEventTypeValues.collapse);
    }

    getBody(event: DataCloudEventData): AiOverviewEventDataCloudAnalyticsEvent {
        const baseEvent = this.baseBody(event);
        const interaction = this.buildEnvelope(event, baseEvent, DataCloudEventTypeValues.collapse);
        interaction.aiOverviewListingCount = requiredListingCount(event);
        return { interaction, user: this.buildUser(baseEvent) };
    }
}

export class AiOverviewMobileToggleDataCloudEventType extends AiOverviewDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return matchesAction(event, DataCloudEventTypeValues.mobileToggle);
    }

    getBody(event: DataCloudEventData): AiOverviewEventDataCloudAnalyticsEvent {
        const baseEvent = this.baseBody(event);
        const interaction = this.buildEnvelope(event, baseEvent, DataCloudEventTypeValues.mobileToggle);
        interaction.aiOverviewListingCount = requiredListingCount(event);
        interaction.aiOverviewExpandSource = event.aiOverviewExpandSource;
        applyOptionalTokenAndTtft(interaction, event);
        return { interaction, user: this.buildUser(baseEvent) };
    }
}

const normalizeErrorToken = (raw: string | undefined): string => {
    return AI_OVERVIEW_ERROR_TOKENS.includes(raw as (typeof AI_OVERVIEW_ERROR_TOKENS)[number])
        ? (raw as string)
        : AI_OVERVIEW_ERROR_TOKEN_FALLBACK;
};

/** Trim, collapse newlines, and cap the free-text error message for telemetry. */
const normalizeMessage = (raw: string | undefined): string =>
    (raw ?? '')
        .replace(/[\r\n]+/g, ' ')
        .trim()
        .slice(0, 500);

export class AiOverviewStreamErrorDataCloudEventType extends AiOverviewDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return matchesAction(event, DataCloudEventTypeValues.streamError);
    }

    getBody(event: DataCloudEventData): AiOverviewEventDataCloudAnalyticsEvent {
        const baseEvent = this.baseBody(event);
        const interaction = this.buildEnvelope(event, baseEvent, DataCloudEventTypeValues.streamError);
        // Narrow regardless of what the component passed so a stale value can't leak.
        interaction.aiOverviewStatus = 'error';
        // Forward the SSE error message when present; opaque HTTP/network drops
        // carry none, so fall back to the taxonomy token to keep it populated.
        const message = normalizeMessage(event.aiOverviewErrorMessage);
        interaction.message = message || normalizeErrorToken(event.aiOverviewErrorToken);
        applyOptionalTokenAndTtft(interaction, event);
        return { interaction, user: this.buildUser(baseEvent) };
    }
}

export class AiOverviewRetryDataCloudEventType extends AiOverviewDataCloudEventType {
    canHandle(event: DataCloudEventData): boolean {
        return matchesAction(event, DataCloudEventTypeValues.retry);
    }

    getBody(event: DataCloudEventData): AiOverviewEventDataCloudAnalyticsEvent {
        const baseEvent = this.baseBody(event);
        const interaction = this.buildEnvelope(event, baseEvent, DataCloudEventTypeValues.retry);
        // Retry fires only from the error state; narrow the status regardless
        // of what the component passed so a stale value can't leak through.
        interaction.aiOverviewStatus = 'error';
        interaction.aiOverviewWasRefreshed = event.aiOverviewWasRefreshed === '1' ? '1' : '0';
        interaction.aiOverviewErrorToken = normalizeErrorToken(event.aiOverviewErrorToken);
        applyOptionalTokenAndTtft(interaction, event);
        return { interaction, user: this.buildUser(baseEvent) };
    }
}
